/**
 * Web Landing Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useLandingPageFacade from @ideoz/core (zero duplication!)
 * - Injects web-specific implementations (navigation, auth)
 * - Passes organized facade to LandingPageTemplate
 * 
 * Benefits:
 * - 90% less code (27 props → 6 grouped props)
 * - Single source of truth for logic
 * - Easy to test (mock one hook)
 * - Platform-agnostic template
 */

import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LandingPageTemplate, showToast } from '@ideoz/ui';
import { useLandingPageFacade } from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';
import Redirect from './Redirect';
import Login from './Login';

export default function LandingPage(): JSX.Element {
  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we need to open the auth dialog (e.g., returning from forgot password)
  useEffect(() => {
    const openDialog = searchParams.get('openDialog');
    if (openDialog === 'login') {
      authDialog.openLogin();
      // Remove the query parameter
      searchParams.delete('openDialog');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, authDialog]);

  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(() => ({
    isOpen: authDialog.isOpen,
    mode: authDialog.mode,
    isLoading: authDialog.isLoading,
    openLogin: authDialog.openLogin,
    openRegister: authDialog.openRegister,
    close: authDialog.closeDialog,
  }), [authDialog]);

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useLandingPageFacade({
    navigation,
    auth,
    showToast,
  });

  return (
    <LandingPageTemplate
      facade={facade}

      // Web-specific overrides
      authDialogState={authDialog}
      isWeb={true}
    />
  );
}

